import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Box from '@carrier-io/air-react/Box';
import Button from '@carrier-io/air-react/Button';
import CountrySelect from '@carrier-io/air-react/patterns/CountrySelect';
import ModalContent from '@carrier-io/air-react/ModalContent';
import FormLabel from '@carrier-io/air-react/FormLabel';
import ModalActions from '@carrier-io/air-react/ModalActions';
import TextField from '@carrier-io/air-react/TextField';
import { Modal, ModalTitle, Typography } from '@carrier-io/air-react';
import Stack from '@carrier-io/air-react/Stack';
import Checkbox from '@carrier-io/air-react/Checkbox';
import InputAdornment from '@carrier-io/air-react/InputAdornment';
import IconButton from '@carrier-io/air-react/IconButton';
import FormControlLabel from '@carrier-io/air-react/FormControlLabel';
import EmailIcon from 'components/icons/email-icon';
import { ConfirmPopup } from './ConfirmPopup';
import { useNotification } from 'context/NotificationContext';
import { TextFieldColor, Variant } from 'constants/componentEnums';
import { NewVendorSchema, UpdatedVendorSchema } from 'store/models/Vendor';
import { NotificationBgColor } from 'store/models/Notification';
import { FieldData, Vendor } from 'store/models/models';
import { CREATE_VENDOR } from '../../store/graphql/mutations/CREATE_VENDOR';
import { UPDATE_VENDOR } from 'store/graphql/mutations/UPDATE_VENDOR';
import {
  isIdValid,
  isValidField,
  validateAgreedFlag,
  validateCity,
  validateCountry,
  validateEmail,
  validateFirstName,
  validateLastName,
  validateName,
  validatePhoneNumber,
  validateState,
  validateStreetAddress,
  validateZipCode,
} from 'utils/validators';
import styles from '../../styles/Popup.module.css';

export type VendorPopupProps = {
  opened: boolean;
  onCancel: () => void;
  onVendorAdded: () => void;
  editableVendor: Vendor | null;
  isEditing?: boolean;
};

const emptyFieldData: FieldData = { value: '', isError: false, errorMessage: '' };

export const VendorPopup = ({ onCancel, opened, onVendorAdded, editableVendor, isEditing }: VendorPopupProps) => {
  const { vendorId } = useParams();
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false);
  // Company name and address states
  const [companyNameData, setCompanyNameData] = useState(emptyFieldData);

  // Contact Info states
  const [cityData, setCityData] = useState(emptyFieldData);
  const [streetAddressData, setStreetAddressData] = useState(emptyFieldData);
  const [zipCodeData, setZipCodeData] = useState(emptyFieldData);
  const [countryData, setCountryData] = useState(emptyFieldData);
  const [stateData, setStateData] = useState(emptyFieldData);

  // Contact person states
  const [firstNameData, setFirstNameData] = useState(emptyFieldData);
  const [lastNameData, setLastNameData] = useState(emptyFieldData);

  const [emailAddressData, setEmailAddressData] = useState(emptyFieldData);
  const [phoneNumberData, setPhoneNumberData] = useState(emptyFieldData);

  const [agreedFlagData, setAgreedFlagData] = useState({ ...emptyFieldData, value: false });

  const [createVendor, { loading: creatingLoading }] = useMutation(CREATE_VENDOR);
  const [updateVendor, { loading: updatingLoading }] = useMutation(UPDATE_VENDOR);

  const isAgreedFlagValid = (agreedFlag: boolean) => {
    const { isValid, errorMessage } = validateAgreedFlag(agreedFlag);

    setAgreedFlagData((prevData) => ({ ...prevData, isError: !isValid, errorMessage }));

    return isValid;
  };

  const resetFields = () => {
    setCompanyNameData(emptyFieldData);

    setStreetAddressData(emptyFieldData);
    setCityData(emptyFieldData);
    setZipCodeData(emptyFieldData);
    setStateData(emptyFieldData);
    setCountryData(emptyFieldData);

    setFirstNameData(emptyFieldData);
    setLastNameData(emptyFieldData);

    setEmailAddressData(emptyFieldData);
    setPhoneNumberData(emptyFieldData);

    setAgreedFlagData({ ...emptyFieldData, value: false });
  };

  const closeConfirmPopup = () => {
    setConfirmPopupOpened(false);
  };

  const openConfirmPopup = () => {
    setConfirmPopupOpened(true);
  };

  const handleClose = () => {
    resetFields();
    onCancel();
    closeConfirmPopup();
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setStateCb: Dispatch<SetStateAction<FieldData>>,
  ) => setStateCb((prevData) => ({ ...prevData, value: event.target.value }));

  const isValidNewVendor = ({
    vendorName,
    firstName,
    lastName,
    email,
    phone,
    contactInfo: { street, city, country, zip, state },
    paperWorkFinished,
  }: NewVendorSchema) =>
    isValidField(vendorName, setCompanyNameData, validateName) &&
    isValidField(firstName, setFirstNameData, validateFirstName) &&
    isValidField(lastName, setLastNameData, validateLastName) &&
    isValidField(email, setEmailAddressData, validateEmail) &&
    isValidField(phone || '', setPhoneNumberData, validatePhoneNumber) &&
    isValidField(street, setStreetAddressData, validateStreetAddress) &&
    isValidField(city, setCityData, validateCity) &&
    isValidField(state || '', setStateData, validateState) &&
    isValidField(zip || '', setZipCodeData, validateZipCode) &&
    isValidField(country, setCountryData, validateCountry) &&
    isAgreedFlagValid(paperWorkFinished);

  const isValidUpdatedVendor = ({ vendorId, vendor }: UpdatedVendorSchema) => {
    const {
      contactInfo: { zip, state, street, country, city },
      firstName,
      lastName,
      phone,
      email,
    } = vendor;

    return (
      isValidField(country || '', setCountryData, validateCountry) &&
      isIdValid(vendorId) &&
      isValidField(street || '', setStreetAddressData, validateStreetAddress) &&
      isValidField(city || '', setCityData, validateCity) &&
      (!zip || isValidField(zip, setZipCodeData, validateZipCode)) &&
      (!state || isValidField(state, setStateData, validateState)) &&
      isValidField(phone || '', setPhoneNumberData, validatePhoneNumber) &&
      isValidField(firstName || '', setFirstNameData, validateFirstName) &&
      isValidField(lastName || '', setLastNameData, validateLastName) &&
      isValidField(email || '', setEmailAddressData, validateEmail)
    );
  };

  const getUpdatedVendor = (
    { vendor, vendorId }: UpdatedVendorSchema,
    editableVendor: Vendor | null,
  ): UpdatedVendorSchema | null => {
    const {
      contactInfo: { zip, state, street, country, city },
      firstName,
      lastName,
      phone,
      email,
    } = vendor;

    const fieldsToCheck = [
      ['firstName', firstName, editableVendor?.firstName],
      ['lastName', lastName, editableVendor?.lastName],
      ['email', email, editableVendor?.email],
      ['phone', phone, editableVendor?.phone],
    ];

    const contactFieldsToCheck = [
      ['street', street, editableVendor?.contactInfo?.street],
      ['city', city, editableVendor?.contactInfo?.city],
      ['country', country, editableVendor?.contactInfo?.country],
      ['state', state, editableVendor?.contactInfo?.state],
      ['zip', zip, `${editableVendor?.contactInfo?.zip}`],
    ];

    const updatedFields = Object.fromEntries(
      fieldsToCheck.filter(([, newVal, oldVal]) => newVal && newVal !== oldVal).map(([key, newVal]) => [key, newVal]),
    );

    const updatedContactFields = Object.fromEntries(
      contactFieldsToCheck
        .filter(([, newVal, oldVal]) => newVal && newVal !== oldVal)
        .map(([key, newVal]) => [key, newVal]),
    );

    const isContactPersonUpdated = Object.keys(updatedFields).length;
    const isContactAddressUpdated = Object.keys(updatedContactFields).length;

    if (!isContactPersonUpdated && !isContactAddressUpdated) {
      return null;
    }

    return {
      vendorId,
      vendor: {
        ...updatedFields,
        ...(isContactAddressUpdated ? { contactInfo: updatedContactFields } : {}),
      },
    };
  };

  const handleCreateVendor = async () => {
    const vendorToSave: NewVendorSchema = {
      vendorName: companyNameData.value,
      firstName: firstNameData.value,
      lastName: lastNameData.value,
      email: emailAddressData.value,
      contactInfo: {
        country: countryData.value,
        city: cityData.value,
        street: streetAddressData.value,
        ...(stateData.value ? { state: stateData.value } : {}),
        ...(zipCodeData.value ? { zip: zipCodeData.value } : {}),
      },
      paperWorkFinished: agreedFlagData.value,
      ...(phoneNumberData.value ? { phone: phoneNumberData.value } : {}),
    };

    const isValidData = isValidNewVendor(vendorToSave);

    if (isValidData) {
      createVendor({
        variables: {
          vendor: vendorToSave,
        },
      })
        .then((response) => {
          if (response.data && !response?.errors?.length) {
            resetFields();
            onVendorAdded();
          }
        })
        .catch((error) => {
          console.error('Error creating vendor:', error.message);

          setTextContent(`There was an issue with your request. Please check the entered data and try again`);
          setNotificationBgColor(NotificationBgColor.ERROR);
          setIsNotificationOpened(true);

          if (error?.message?.includes('vendorName already exists')) {
            setCompanyNameData((prevData) => ({
              ...prevData,
              errorMessage: 'Company Name already exists',
              isError: true,
            }));
          }
        });
    }
  };

  const handleUpdateVendor = async () => {
    const updatedVendorData: UpdatedVendorSchema = {
      vendorId: vendorId || '',
      vendor: {
        firstName: firstNameData.value,
        lastName: lastNameData.value,
        phone: phoneNumberData.value,
        email: emailAddressData.value,
        contactInfo: {
          street: streetAddressData.value,
          city: cityData.value,
          country: countryData.value,
          zip: zipCodeData.value,
          state: stateData.value,
        },
      },
    };

    const isValid = isValidUpdatedVendor(updatedVendorData);

    if (isValid) {
      const updatedVendor = getUpdatedVendor(updatedVendorData, editableVendor);

      if (!updatedVendor) {
        setTextContent(`No changes have been saved. Please make updates before submitting`);
        setNotificationBgColor(NotificationBgColor.WARNING);
        setIsNotificationOpened(true);
        return;
      }

      try {
        await updateVendor({
          variables: updatedVendor,
        });

        setTextContent(`${companyNameData.value || 'The vendor'} has been successfully updated`);
        setNotificationBgColor(NotificationBgColor.SUCCESS);
        setIsNotificationOpened(true);
        onVendorAdded?.();
        handleClose();
      } catch (err) {
        setTextContent(`There was an issue with your request. Please check the entered data and try again`);
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);
        console.error('Update Vendor Error: ', err);
      }
    }
  };

  const handleSubmit = () => {
    if (isEditing) {
      handleUpdateVendor();
    } else {
      handleCreateVendor();
    }
  };

  useEffect(() => {
    if (isEditing && editableVendor) {
      const {
        vendorName,
        firstName,
        lastName,
        email,
        contactInfo: { country, city, street, state, zip },
        paperWorkFinished,
        phone,
      } = editableVendor;
      setCompanyNameData((prevData) => ({ ...prevData, value: vendorName }));
      setFirstNameData((prevData) => ({ ...prevData, value: firstName }));
      setLastNameData((prevData) => ({ ...prevData, value: lastName }));
      setEmailAddressData((prevData) => ({ ...prevData, value: email }));
      setCountryData((prevData) => ({ ...prevData, value: country }));
      setCityData((prevData) => ({ ...prevData, value: city }));
      setStreetAddressData((prevData) => ({ ...prevData, value: street }));
      setPhoneNumberData((prevData) => ({ ...prevData, value: phone || '' }));
      setAgreedFlagData((prevData) => ({ ...prevData, value: paperWorkFinished }));
      setZipCodeData((prevData) => ({ ...prevData, value: zip ? `${zip}` : '' }));
      setStateData((prevData) => ({ ...prevData, value: state || '' }));
    }
  }, [isEditing, opened]);

  return (
    <Modal open={opened} fullWidth maxWidth="sm" scroll="body">
      <ModalTitle
        data-testid="vendor-modal-title"
        maxWidth="md"
        className={styles.popupTitle}
        clearable
        onClose={openConfirmPopup}
      >
        <Stack direction="row" justifyContent="space-between" width={'100%'}>
          <Typography variant={Variant.H6} fontWeight={600}>
            {isEditing ? 'Edit Vendor Details' : 'Add a new vendor'}
          </Typography>
        </Stack>
      </ModalTitle>
      <ModalContent style={{ paddingBottom: '0px' }}>
        <Box display="flex" flexDirection="column" mb={2}>
          <FormLabel>
            <Typography variant={Variant.Body1} color={'#060F16'}>
              Company Name
            </Typography>
          </FormLabel>
          <TextField
            data-testid="company-name-form"
            required={true}
            disabled={isEditing}
            label="Company Name"
            type="text"
            color={TextFieldColor.Primary}
            showBorder
            value={companyNameData.value}
            onChange={(event) => handleChange(event, setCompanyNameData)}
            onBlur={() => isValidField(companyNameData.value, setCompanyNameData, validateName)}
            hideBackgroundColor
            helperText={companyNameData.errorMessage}
            error={companyNameData.isError}
          />
        </Box>
        <Box display="flex" flexDirection="column" mb={2} width="100%">
          <FormLabel>
            <Typography variant={Variant.Body1} color={'#060F16'}>
              Contact Person
            </Typography>
          </FormLabel>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <TextField
                data-testid="first-name-form"
                value={firstNameData.value}
                required={true}
                label="First Name"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                onChange={(event) => handleChange(event, setFirstNameData)}
                onBlur={() => isValidField(firstNameData.value, setFirstNameData, validateFirstName)}
                helperText={firstNameData.errorMessage}
                error={firstNameData.isError}
                hideBackgroundColor
              />
              <TextField
                data-testid="last-name-form"
                value={lastNameData.value}
                required={true}
                onChange={(event) => handleChange(event, setLastNameData)}
                onBlur={() => isValidField(lastNameData.value, setLastNameData, validateLastName)}
                label="Last Name"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                hideBackgroundColor
                helperText={lastNameData.errorMessage}
                error={lastNameData.isError}
              />
            </Stack>
            <TextField
              data-testid="email-address-form"
              value={emailAddressData.value}
              className={styles.emailField}
              required={true}
              onChange={(event) => handleChange(event, setEmailAddressData)}
              onBlur={() => isValidField(emailAddressData.value, setEmailAddressData, validateEmail)}
              label="Email"
              type="text"
              color={TextFieldColor.Primary}
              showBorder
              hideBackgroundColor
              helperText={emailAddressData.errorMessage}
              error={emailAddressData.isError}
              inputSetting={{
                endAdornment: (
                  <InputAdornment className={styles.emailIcon} disableTypography position="start" variant="standard">
                    <IconButton>
                      <EmailIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              data-testid="phone-number-form"
              value={phoneNumberData.value}
              onChange={(event) => handleChange(event, setPhoneNumberData)}
              label="Phone"
              type="text"
              color={TextFieldColor.Primary}
              showBorder
              hideBackgroundColor
              helperText={phoneNumberData.errorMessage}
              error={phoneNumberData.isError}
            />
          </Stack>
        </Box>
        <Box display="flex" flexDirection="column" mb={2} width="100%" style={{ marginBottom: '0px' }}>
          <FormLabel>
            <Typography variant="body1" color={'#060F16'}>
              Company Address
            </Typography>
          </FormLabel>
          <Stack direction="column" spacing={1}>
            <TextField
              data-testid="street-address-form"
              value={streetAddressData.value}
              required={true}
              label="Street Address"
              type="text"
              color={TextFieldColor.Primary}
              showBorder
              onChange={(event) => handleChange(event, setStreetAddressData)}
              onBlur={() => isValidField(streetAddressData.value, setStreetAddressData, validateStreetAddress)}
              error={streetAddressData.isError}
              helperText={streetAddressData.errorMessage}
              hideBackgroundColor
            />
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <TextField
                data-testid="city-form"
                style={{ width: '340px' }}
                value={cityData.value}
                required={true}
                onChange={(event) => handleChange(event, setCityData)}
                onBlur={() => isValidField(cityData.value, setCityData, validateCity)}
                label="City"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                hideBackgroundColor
                helperText={cityData.errorMessage}
                error={cityData.isError}
              />
              <TextField
                data-testid="zip-code-form"
                style={{ width: '180px' }}
                value={zipCodeData.value}
                label="ZIP Code"
                onChange={(event) => handleChange(event, setZipCodeData)}
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                hideBackgroundColor
                error={zipCodeData.isError}
                helperText={zipCodeData.errorMessage}
              />
            </Stack>
            <Stack className={styles.countryBox} direction="row" justifyContent="space-between" spacing={1}>
              <CountrySelect
                data-testid="country-select-form"
                value={countryData.value}
                required={true}
                label="Country"
                onSelect={(value) =>
                  setCountryData((prevData) => ({ ...prevData, value: value instanceof Array ? value[0] : value }))
                }
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                error={countryData.isError}
                helperText={countryData.errorMessage}
                hideBackgroundColor
              />
              <TextField
                data-testid="state-form"
                value={stateData.value}
                onChange={(event) => handleChange(event, setStateData)}
                label="State"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                hideBackgroundColor
                error={stateData.isError}
                helperText={stateData.errorMessage}
              />
            </Stack>
            <FormControlLabel
              disabled={isEditing}
              control={
                <Checkbox
                  data-testid="agreement-form"
                  color="base"
                  checked={agreedFlagData.value}
                  onChange={(event, checked) => setAgreedFlagData((prevData) => ({ ...prevData, value: checked }))}
                />
              }
              label="Vendor agreement is confirmed"
              labelPlacement="end"
            />
            {agreedFlagData.isError && (
              <Typography variant="caption" color="error">
                {agreedFlagData.errorMessage}
              </Typography>
            )}
          </Stack>
        </Box>
      </ModalContent>
      <ModalActions className={styles.modalActions}>
        <Button data-testid="cancel-vendor-form" variant="text" color="secondary" onClick={openConfirmPopup}>
          Cancel
        </Button>
        <Button
          data-testid="save-vendor-form"
          disabled={creatingLoading || updatingLoading}
          variant="outlined"
          color="primary"
          onClick={handleSubmit}
        >
          Save Vendor
        </Button>
      </ModalActions>
      <ConfirmPopup onCancel={closeConfirmPopup} onConfirm={handleClose} opened={confirmPopupOpened} />
    </Modal>
  );
};
